// Helpers for handling artist disciplines / tags across the site.
// An artist can have a primary `discipline` string AND an array of extra `disciplines`.
// All comparisons are case-insensitive; dedup preserves the first casing we see.

export function artistDisciplines(artist) {
    if (!artist) return [];
    const primary = (artist.discipline || "").trim();
    const extras = Array.isArray(artist.disciplines) ? artist.disciplines : [];
    const list = [primary, ...extras].map((d) => (d || "").trim()).filter(Boolean);
    // dedup within a single artist (case-insensitive), keep first casing
    const seen = new Set();
    const out = [];
    for (const d of list) {
        const k = d.toLowerCase();
        if (!seen.has(k)) {
            seen.add(k);
            out.push(d);
        }
    }
    return out;
}

// Given the full artists list, return `["all", ...unique disciplines]`
// dedup is case-insensitive; sort is alphabetical (case-insensitive)
export function allDisciplines(artists) {
    const map = new Map(); // key: lowercase, value: original casing (first seen)
    (artists || []).forEach((a) => {
        artistDisciplines(a).forEach((d) => {
            const k = d.toLowerCase();
            if (!map.has(k)) map.set(k, d);
        });
    });
    const list = Array.from(map.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return ["all", ...list];
}

// Does the artist have this discipline (case-insensitive)?
export function artistHasDiscipline(artist, target) {
    if (!target || target === "all") return true;
    const t = target.toLowerCase();
    return artistDisciplines(artist).some((d) => d.toLowerCase() === t);
}

// Split artists by their `role` field. Collaborators appear in a separate section on /artists.
export function splitByRole(artists) {
    const list = artists || [];
    return {
        artists: list.filter((a) => (a.role || "artist") === "artist"),
        collaborators: list.filter((a) => a.role === "collaborator"),
    };
}

// Given a name in an event lineup, find the matching artist (or collaborator) by name.
// Returns the artist doc if found, otherwise null. Case-insensitive.
export function findArtistByName(name, artists) {
    if (!name || !artists || !artists.length) return null;
    const target = name.trim().toLowerCase();
    if (!target) return null;
    return artists.find((a) => (a.name || "").trim().toLowerCase() === target) || null;
}

